import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import siteAssets from "@/lib/siteAssets";
import desktopLogo from "@/assets/branding/logo-desktop-light.svg";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.reload();
  };

  const navLinks = [
    { label: "Vanliga frågor", href: "#faq" },
    { label: "Kontakt", href: "#book" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled ? "py-2" : "py-0"
      }`}
    >
        <div
          className={`mx-auto transition-all duration-500 ease-out ${
            scrolled
              ? "max-w-[640px] nav-pill-scrolled"
              : "max-w-none w-full"
          }`}
          style={
            scrolled
              ? undefined
              : {
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                }
          }
        >
        <div
          className={`flex items-center transition-all duration-500 ease-out ${
            scrolled ? "h-14 px-4" : "h-20 px-6 md:px-10"
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            onClick={handleLogoClick}
            aria-label="KitchenFlow — Till startsidan"
            className="shrink-0 mr-auto flex items-center"
          >
            <img
              src={(!scrolled) ? desktopLogo : siteAssets.logo}
              alt="KitchenFlow"
              className={`hidden md:block w-auto object-contain transition-all duration-500 ${
                scrolled ? "h-16" : "h-20"
              }`}
            />
            <img
              src={siteAssets.logo}
              alt="KitchenFlow"
              className={`md:hidden w-auto object-contain transition-all duration-500 ${
                scrolled ? "h-16" : "h-20"
              }`}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-200 rounded-lg ${
                  scrolled
                    ? "text-[hsl(220,10%,30%)] hover:text-[hsl(220,15%,15%)] hover:bg-black/[0.04]"
                    : "text-white hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/login"
              className={`px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-200 rounded-lg ${
                scrolled
                  ? "text-[hsl(220,10%,30%)] hover:text-[hsl(220,15%,15%)] hover:bg-black/[0.04]"
                  : "text-white hover:text-white hover:bg-white/10"
              }`}
            >
              Logga in
            </Link>
            <div className="ml-2">
              <a href="#book">
                <Button
                  size="sm"
                  style={{ backgroundColor: "#026133" }}
                  className="cta-glow text-[13px] font-medium rounded-full h-8 px-5 text-white hover:brightness-110 border-0"
                >
                  Boka möte
                </Button>
              </a>
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 -mr-1 text-[hsl(220,10%,35%)] hover:text-[hsl(220,15%,15%)] transition-colors rounded-lg hover:bg-black/[0.04]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-black/[0.06] px-5 pb-5 pt-2 space-y-0.5">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-2.5 px-2 text-sm text-[hsl(220,10%,35%)] hover:text-[hsl(220,15%,15%)] transition-colors rounded-lg hover:bg-black/[0.04]"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 pt-3 mt-2 border-t border-black/[0.06]">
              <Link to="/login" className="flex-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-10 text-sm rounded-xl border-black/[0.1] bg-black/[0.03] text-[hsl(220,10%,30%)] hover:text-[hsl(220,15%,15%)] hover:bg-black/[0.06]"
                >
                  Logga in
                </Button>
              </Link>
              <a href="#book" className="flex-1" onClick={() => setMobileOpen(false)}>
                <Button
                  size="sm"
                  style={{ backgroundColor: "#026133" }}
                  className="cta-glow w-full h-10 text-sm rounded-full text-white hover:brightness-110"
                >
                  Demo
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
